import { useUser } from "@auth0/nextjs-auth0";
import {
  InteractionType,
  ReactActionType
} from "@input-output-hk/essential-cardano-models";
import { youtubeShortsHack } from "@input-output-hk/essential-cardano-utils";
import { GetServerSideProps } from "next";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import React from "react";
import ReactMarkdown from "react-markdown";
import ReactPlayer from "react-player";
import styled from "styled-components";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import Button from "../../components/buttons/Button";
import CommentInput from "../../components/comments/CommentInput";
import CommentsList from "../../components/comments/CommentsList";
import { EnhancedViewer } from "../../components/EnhancedViewer";
import Heading from "../../components/heading/Heading";
import IconComment from "../../components/icons/Comment";
import External from "../../components/icons/External";
import { ArticleMeta } from "../../components/item/ArticleMeta";
import ContributorsList from "../../components/item/ContributorsList";
import { FAQBackground, FAQTitle } from "../../components/item/FAQBackground";
import { getFaqColorFromId } from "../../components/item/FAQBackground.helpers";
import {
  GlossaryBackground,
  GlossaryTitle
} from "../../components/item/GlossaryBackground";
import { getGlossaryVariantFromId } from "../../components/item/GlossaryBackground.helpers";
import SuggestContributionCta from "../../components/item/SuggestContributionCta";
import { PageContainer } from "../../components/page-partials/PageContainer";
import DummyEmbed from "../../components/podcast/DummyEmbed";
import SocialIcons from "../../components/social-icons/SocialIcons";
import { Stack } from "../../components/Stack";
import Text from "../../components/text/Text";
import { VerifiedContentLabel } from "../../components/verified-content-ribbon/VerifiedContentRibbon";
import { useLoginPrompt } from "../../context/LoginPromptContext";
import createServerContext from "../../context/server-context";
import { findComments, findItemBySlug } from "../../data/db";
import { Item, ItemComment } from "../../data/db/types";
import useItem from "../../hooks/useItem";
import useItemViewMetric from "../../hooks/useItemViewMetric";
import useMutateComment from "../../hooks/useMutateComment";
import useVote from "../../hooks/useVote";
import { setCacheHeader } from "../../page-cache";
import { NEXT_PUBLIC_ENABLE_COMMENTS } from "../../public-config";
import { pxToRem } from "@input-output-hk/px-to-rem";
import {
  forPhoneOnly,
  forTabletPortraitDown,
  forTabletPortraitUp
} from "../../tokens/media-queries";
import { getPrefferedImageUrl, isSupported } from "../../utils/image";
import { maybeGetOriginalVideoUrl } from "../../utils/inline-video";
import { toJsonLd } from "../../utils/json-ld";
import { truncateText } from "../../utils/truncateText";
import { getHostname, getOrigin } from "../../utils/url-utils";
import { isBlessedUser } from "../../utils/user-role";
import rehypeRaw from "rehype-raw";

/**
 * This is conditionally loaded, so only blessed users
 * receive the code that can "do things"
 */
const BlessedOptions = dynamic(
  () => import("../../components/blessed/BlessedOptions")
);

// const Element = styled.div`
//   max-width: var(--responsive-content-inner-width);
// `;

const VideoWrapper = styled.div`
  position: relative;
  padding-top: 56.25%;

  iframe {
    border-radius: ${pxToRem(15)};
  }
`;

const VideoPlayer = styled(ReactPlayer)`
  position: absolute;
  top: 0;
  left: 0;
`;

const ArticleContainer = styled.div`
  position: relative;
  @media ${forPhoneOnly} {
    [data-split] {
      display: none;
    }
  }
`;

const InnerContentContainer = styled.div`
  margin: var(--spacing-xlarge) 0;
  display: grid;

  /**
   * Using 3 columns to perfectly balance
   * center content without crazy minmax/calc hacks:
   *
   * Column 1: Share icons
   * Column 2: Content
   * Column 3: White-space to balance share icons
   */
  grid-template-columns: 1fr var(--default-content-max-width) 1fr;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const InnerContent = styled.div``;

const Article = styled.article`
  position: relative;

  .social-links-desktop {
    display: none;

    @media (min-width: 960px) {
      display: block;
    }
  }

  .social-links-mobile {
    display: none;

    @media (max-width: 960px) {
      margin: 0;
      display: block;
    }
  }
`;
const HeaderContainer = styled.div`
  display: flex;
  margin-top: var(--spacing-large);
  margin-bottom: var(--spacing-large);
  > h1 {
    max-width: ${pxToRem(850)};
    @media ${forTabletPortraitDown} {
      font-size: ${pxToRem(26)};
      line-height: ${pxToRem(35)};
    }
  }
`;
const HeaderShareLinks = styled.div`
  margin-left: auto;
  padding-left: var(--spacing-large);
  @media ${forTabletPortraitDown} {
    display: none;
  }
  & [data-social-icon-set] {
    --social-icon-gap: var(--spacing-default);
  }
`;

const ContentShareLinks = styled.div`
  @media ${forTabletPortraitUp} {
    margin-top: ${pxToRem(145)};

    & [data-social-icon-set] {
      flex-direction: row;
      position: sticky;
      top: ${pxToRem(90)};
    }
  }
  @media ${forTabletPortraitDown} {
    width: initial;
    display: inline-flex;
    align-items: center;
    margin-top: 0;

    & [data-social-icon-set] {
      flex-direction: row;
      position: static;
    }
  }
`;

const ArticleFooterContainer = styled.section`
  max-width: var(--default-content-max-width);
  margin-top: var(--spacing-xxlarge);
`;

const CommentHeader = styled.section`
  color: var(--theme-color-primary);
  padding-bottom: var(--spacing-large);
  margin-bottom: var(--spacing-xlarge);
  border-bottom: var(--card-stroke);
  display: flex;
  align-items: center;
  [data-icon] {
    margin-right: var(--spacing-default);
  }
`;

const LoginHeading = styled(Heading)`
  margin: 0;
  line-height: 1.25;
`;

export const ArticleMarkdown = styled(ReactMarkdown)`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: var(--default-text-weight);
  }
  & img {
    max-width: 100%;
  }

  > *:first-child {
    margin-top: 0;
    padding-top: 0;
  }
`;

const MarkdownAnchor: React.FC<React.AnchorHTMLAttributes<unknown>> = (
  props
) => {
  const { href } = props;
  const videoUrl = href ? maybeGetOriginalVideoUrl(href) : null;

  if (videoUrl) {
    return (
      <VideoWrapper>
        <VideoPlayer url={videoUrl} width="100%" height="100%" controls />
      </VideoWrapper>
    );
  } else {
    return <a {...props} target="_blank" rel="noopener nofollow" />;
  }
};

type Props = {
  origin: string | null;
  item: Item | null;
  comments: ItemComment[] | null;
};
const ItemPage: React.FC<Props> = ({
  item: _item,
  comments: _comments,
  origin = getOrigin()
}) => {
  const { user } = useUser();
  const { showPrompt } = useLoginPrompt();
  const isBlessed = isBlessedUser(user);
  const router = useRouter();
  const itemUrl = origin + router.asPath;
  const {
    item,
    comments,
    mutate: refetchItem
  } = useItem(router.query.slug as string, {
    fallbackData: {
      item: _item,
      comments: _comments
    }
  });
  /**
   * Record a "page view" for the item
   */
  useItemViewMetric(item?._id);

  const upvoteCount =
    item?.interactionStatistic.find(
      (v) => v.interactionType === InteractionType.UPVOTE
    )?.userInteractionCount || 0;
  const downvoteCount =
    item?.interactionStatistic.find(
      (v) => v.interactionType === InteractionType.DOWNVOTE
    )?.userInteractionCount || 0;
  const { vote, unvote, isLoading } = useVote(item?._id);
  const myVote = item?._userAction?.["@type"];
  const handleClickVote = user
    ? async (type: ReactActionType) => {
        const res = await (myVote === type ? unvote(type) : vote(type));
        if (res && "action" in res && "object" in res) {
          refetchItem({
            comments,
            item: {
              ...item!,
              _upvoteScore: res.object._upvoteScore,
              _userAction: res.action
            }
          });
        }
      }
    : () =>
        showPrompt(
          <>
            <LoginHeading level="3">Every vote counts</LoginHeading>
            <Text level="2">
              Login to cast your vote
              <br />
              and much more
            </Text>
          </>
        );

  const { isSubmitting, ...commentInputProps } = useMutateComment(
    item
      ? {
          about: item._id
        }
      : undefined,
    {
      onSuccess() {
        refetchItem();
      }
    }
  );

  if (!item) {
    return null;
  }

  const image = item.image;

  const verifiedContentElement = item?._verifiedContent ? (
    <VerifiedContentLabel />
  ) : null;

  const adminElement = isBlessed ? (
    <BlessedOptions item={item} onChanged={() => refetchItem()} />
  ) : null;

  const socialIcons = (
    <SocialIcons
      shareData={{
        url: itemUrl,
        text: item.name,
        image: image?.url || "/essential-cardano-logo.png"
      }}
    />
  );
  const headingElement = (
    <HeaderContainer>
      <Heading level="1" isTight>
        {item.name}
      </Heading>
      <HeaderShareLinks>{socialIcons}</HeaderShareLinks>
    </HeaderContainer>
  );

  const artcleMeta = (
    <ArticleMeta
      item={item}
      verifiedContentElement={verifiedContentElement}
      myVote={myVote}
      handleClickVote={handleClickVote}
      isLoading={isLoading}
      upvoteCount={upvoteCount}
      downvoteCount={downvoteCount}
      adminElement={adminElement}
    />
  );

  const videoElement = item.video ? (
    <VideoWrapper>
      <VideoPlayer
        url={youtubeShortsHack(item.video.url)}
        width="100%"
        height="100%"
        controls
      />
    </VideoWrapper>
  ) : null;

  const imageElement =
    image && isSupported(image.url) ? (
      <EnhancedViewer
        src={getPrefferedImageUrl(image)}
        alt={item.name}
        width={image.width}
        height={image.height}
        blurDataURL={image._blurDataURL}
      />
    ) : null;

  const markdownElement = (
    <ArticleMarkdown
      components={{ a: MarkdownAnchor as any }}
      rehypePlugins={[rehypeRaw]}
    >
      {item.articleBody}
    </ArticleMarkdown>
  );

  const shareElementDesktop = (
    <ContentShareLinks className="social-links-desktop">
      {socialIcons}
    </ContentShareLinks>
  );

  const shareElementMobile = (
    <ContentShareLinks className="social-links-mobile">
      {socialIcons}
    </ContentShareLinks>
  );

  const shareElement = <ContentShareLinks>{socialIcons}</ContentShareLinks>;

  const articleFooterElement = (
    <>
      <ArticleFooterContainer>
        <Stack level="1">
          {NEXT_PUBLIC_ENABLE_COMMENTS && (
            <>
              <a id="comments-section"></a>
              <CommentHeader>
                <IconComment /> {item.commentCount} Comments
              </CommentHeader>
              {comments && (
                <CommentsList
                  comments={comments}
                  onCommentChange={() => refetchItem()}
                />
              )}
              {user ? (
                <CommentInput
                  disabled={isSubmitting}
                  user={user}
                  {...commentInputProps}
                />
              ) : (
                <CommentInput
                  onFocus={() =>
                    showPrompt(
                      <>
                        <LoginHeading level="3">
                          Join the conversation
                        </LoginHeading>
                        <Text level="2">
                          Login to comment
                          <br />
                          and much more
                        </Text>
                      </>
                    )
                  }
                />
              )}
            </>
          )}
          <a id="contributors"></a>
          <ContributorsList
            contributors={item.contributor}
            contributionMeta={item._contributions}
          />
          <SuggestContributionCta item={item} />
        </Stack>
      </ArticleFooterContainer>
    </>
  );

  let content: React.ReactNode;

  switch (item._category) {
    case "faq":
      content = (
        <>
          {artcleMeta}
          <Article>
            <FAQBackground size="large" variant={getFaqColorFromId(item._id)}>
              <FAQTitle>{item.name}</FAQTitle>
              {markdownElement}
            </FAQBackground>
            <InnerContentContainer>
              {shareElement}
              <InnerContent>{articleFooterElement}</InnerContent>
            </InnerContentContainer>
          </Article>
        </>
      );
      break;
    case "glossary":
      content = (
        <>
          {artcleMeta}
          <Article>
            <GlossaryBackground
              size="large"
              variant={getGlossaryVariantFromId(item._id)}
            >
              <GlossaryTitle>{item.name}</GlossaryTitle>
              {markdownElement}
            </GlossaryBackground>
            <InnerContentContainer>
              {shareElement}
              <InnerContent>{articleFooterElement}</InnerContent>
            </InnerContentContainer>
          </Article>
        </>
      );
      break;
    case "article":
      content = (
        <>
          {headingElement}
          {artcleMeta}
          <Article>
            <InnerContentContainer>
              {shareElementDesktop}
              <InnerContent>
                {imageElement}
                {shareElementMobile}
                {markdownElement}
                {articleFooterElement}
              </InnerContent>
            </InnerContentContainer>
          </Article>
        </>
      );
      break;
    case "other":
      const hostname = item.url ? getHostname(item.url) : "";
      content = (
        <>
          {headingElement}
          {artcleMeta}
          <Article>
            <InnerContentContainer>
              {shareElementDesktop}
              <InnerContent>
                {imageElement}
                {shareElementMobile}
                {markdownElement}
                {hostname && (
                  <Button
                    as="a"
                    href={item.url}
                    size="large"
                    target="_blank"
                    rel="noopener"
                  >
                    Visit link ({hostname}) <External width="24" height="24" />
                  </Button>
                )}
                {articleFooterElement}
              </InnerContent>
            </InnerContentContainer>
          </Article>
        </>
      );
      break;
    case "video":
      content = (
        <>
          {headingElement}
          {artcleMeta}
          <Article>
            <InnerContentContainer>
              {shareElementDesktop}
              <InnerContent>
                {videoElement}
                {shareElementMobile}
                {markdownElement}
                {articleFooterElement}
              </InnerContent>
            </InnerContentContainer>
          </Article>
        </>
      );
      break;
    case "podcast":
      content = (
        <>
          {headingElement}
          {artcleMeta}
          <Article>
            <DummyEmbed title={item.name} href={item.url!} />
            <InnerContentContainer>
              {shareElementDesktop}
              <InnerContent>
                {markdownElement}
                {shareElementMobile}
                {articleFooterElement}
              </InnerContent>
            </InnerContentContainer>
          </Article>
        </>
      );
      break;
    default:
      content = (
        <>
          {headingElement}
          {artcleMeta}
          <Article>
            <InnerContentContainer>
              {shareElementDesktop}
              <InnerContent>
                {item.video ? videoElement : image ? imageElement : null}
                {shareElementMobile}
                {markdownElement}
                {articleFooterElement}
              </InnerContent>
            </InnerContentContainer>
          </Article>
        </>
      );
  }

  /**
   * Use the article "plain" content
   * without markdown for machines/scrapers/bots
   */
  const plainContent = item._text ?? "";

  const truncated = truncateText(plainContent, 20);

  return (
    <>
      <Breadcrumbs
        breadcrumbArgument={{
          argumentType: "articleName",
          argumentContents: item.name
        }}
      />
      <PageContainer>
        <Head>
          <title>{item.name} | Essential Cardano</title>
          <meta name="description" content={truncated} />
          <link rel="canonical" href={itemUrl} key="canonical" />
          {/* Twitter */}
          <meta
            name="twitter:card"
            content="summary_large_image"
            key="twcard"
          />
          <meta property="og:title" content={item.name} />
          <meta
            property="og:description"
            content={truncated}
            key="ogdescription"
          />
          <meta property="og:url" content={itemUrl} key="ogurl" />
          <meta
            property="og:image"
            content={
              item._ogImageUrl ??
              item.image?.url ??
              "/essential-cardano-logo.png"
            }
            key="ogimage"
          />

          <link rel="icon" href="/favicon.ico" />
          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            href="/favicon-32x32.png"
          />
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href="/favicon-16x16.png"
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                ...toJsonLd(item)
              })
            }}
          />
        </Head>
        <ArticleContainer data-category={item._category}>
          <Stack level="1">{content}</Stack>
        </ArticleContainer>
      </PageContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (
  baseCtx
) => {
  const ctx = createServerContext(baseCtx);
  const slug = baseCtx.query.slug as string;
  try {
    const item = await findItemBySlug(ctx, slug);
    const comments = await findComments(ctx, item._id);

    setCacheHeader(ctx, baseCtx.res);

    return {
      props: {
        item: item,
        origin: getOrigin(baseCtx.req),
        comments
      }
    };
  } catch (err) {
    console.error(`Cannot gSSP ${slug}`);
    console.error(err);
  }
  return {
    notFound: true
  };
};

export default ItemPage;
